import { useState, useEffect } from 'react'
import { Plus, X, Shield, Mail } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { PeopleSelect } from '@/components/projects/CreateProjectModal'
import { personFromName, type Person } from '@/components/tickets/data'
import { api } from '@/services/api'
import { fetchClientsWithEmails, formatClientLabel, type ClientWithEmails } from '@/services/clients'
import {
  ProjectStatus,
  ProjectStatusLabels,
  enumToSelectOptions,
} from '@/utils/enum'
import { useAuth } from '@/contexts/AuthContext'

export interface User {
  id: string
  nom: string
  email: string
  role: string
}

export interface ActivitySector {
  id: number
  nom: string
}

export interface ProjectFormData {
  clientId?: string
  clientName?: string
  clientEmail?: string
  clientNom?: string
  clientEmailExisting?: string
  newClientEmails?: string[]  // Emails à ajouter au client sélectionné
  projectManagerId?: string  // Chef de projet principal (= premier de projectManagerIds)
  projectManagerIds?: string[]
  nomProjet: string
  dateKickoff: string
  description: string
  descriptionInterne?: string
  stackTechnique: string
  developpeurs: string
  developerIds?: string[]
  urlGithub: string
  urlServeur: string
  urlFigma?: string
  activitySectorId?: string
  status?: string
  visibility?: string
  // Arrays for multiple URLs
  githubUrls?: string[]
  serverUrls?: string[]
  figmaUrls?: string[]
  notionUrls?: string[]
}

export interface UpdateProjectModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: ProjectFormData) => void | Promise<void>
  isLoading?: boolean
  project: {
    id: number
    nom: string
    description: string | null
    description_interne?: string | null
    date_kickoff: string
    stack_technique: string | null
    noms_devs: string | null
    developer_ids?: string[]
    status: string
    visibility?: string
    client_id: string
    project_manager_id: string
    project_manager_ids?: string[]
    activity_sector_id: number | null
    urls: Array<{ url_type: string; url: string }>
  }
}

export function UpdateProjectModal({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
  project,
}: UpdateProjectModalProps) {
  const { user } = useAuth()
  const isSuperAdmin = user?.email?.endsWith('@qolaig.com') || user?.email?.endsWith('@qolaig.fr')
  
  const [clients, setClients] = useState<ClientWithEmails[]>([])
  const [projectManagers, setProjectManagers] = useState<User[]>([])
  const [people, setPeople] = useState<Person[]>([])
  const [developerIds, setDeveloperIds] = useState<string[]>([])
  const [projectManagerIds, setProjectManagerIds] = useState<string[]>([])
  const [loadingPeople, setLoadingPeople] = useState(false)
  const [activitySectors, setActivitySectors] = useState<ActivitySector[]>([])
  const [loadingClients, setLoadingClients] = useState(false)
  const [loadingManagers, setLoadingManagers] = useState(false)
  const [loadingSectors, setLoadingSectors] = useState(false)
  const [showNewSectorInput, setShowNewSectorInput] = useState(false)
  const [creatingSector, setCreatingSector] = useState(false)
  const [newSectorName, setNewSectorName] = useState('')

  // State for multiple URLs
  const [githubUrls, setGithubUrls] = useState<string[]>([''])
  const [serverUrls, setServerUrls] = useState<string[]>([''])
  const [figmaUrls, setFigmaUrls] = useState<string[]>([''])
  const [notionUrls, setNotionUrls] = useState<string[]>([''])
  const [newClientEmails, setNewClientEmails] = useState<string[]>([])

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

  // Get all URLs by type
  const getUrlsByType = (urls: Array<{ url_type: string; url: string }>, type: string): string[] => {
    const filteredUrls = urls.filter(u => u.url_type === type).map(u => u.url)
    return filteredUrls.length > 0 ? filteredUrls : ['']
  }

  const [formData, setFormData] = useState<ProjectFormData>({
    clientId: '',
    clientName: '',
    clientEmail: '',
    clientNom: '',
    clientEmailExisting: '',
    projectManagerId: '',
    nomProjet: '',
    dateKickoff: '',
    description: '',
    stackTechnique: '',
    developpeurs: '',
    urlGithub: '',
    urlServeur: '',
        urlFigma: '',
        activitySectorId: '',
        status: '',
        descriptionInterne: '',
      })

  // Initialize form data when modal opens or project changes
  useEffect(() => {
    if (open && project) {
      // Format date for input (YYYY-MM-DD)
      const dateKickoff = project.date_kickoff ? new Date(project.date_kickoff).toISOString().split('T')[0] : ''
      
      // Get all URLs by type
      const ghUrls = getUrlsByType(project.urls, 'github')
      const srvUrls = getUrlsByType(project.urls, 'server')
      const fgUrls = getUrlsByType(project.urls, 'figma')
      const notionUrlsFromProject = getUrlsByType(project.urls, 'notion')

      setGithubUrls(ghUrls)
      setServerUrls(srvUrls)
      setFigmaUrls(fgUrls)
      setNotionUrls(notionUrlsFromProject)

      setFormData({
        clientId: project.client_id || '',
        clientNom: '',
        clientEmailExisting: '',
        projectManagerId: project.project_manager_id || '',
        nomProjet: project.nom || '',
        dateKickoff: dateKickoff,
        description: project.description || '',
        descriptionInterne: project.description_interne || '',
        stackTechnique: project.stack_technique || '',
        developpeurs: project.noms_devs || '',
        urlGithub: ghUrls[0] || '',
        urlServeur: srvUrls[0] || '',
        urlFigma: fgUrls[0] || '',
        activitySectorId: project.activity_sector_id ? String(project.activity_sector_id) : '',
        status: project.status || '',
      })
      setDeveloperIds(project.developer_ids ?? [])
      setProjectManagerIds(
        project.project_manager_ids?.length
          ? project.project_manager_ids
          : project.project_manager_id
            ? [project.project_manager_id]
            : []
      )
    }
  }, [open, project])

  useEffect(() => {
    if (open) setNewClientEmails([])
  }, [open])

  // Fetch clients, project managers and activity sectors when modal opens
  useEffect(() => {
    if (open) {
      fetchClients()
      fetchProjectManagers()
      fetchActivitySectors()
      fetchPeople()
    }
  }, [open])

  const fetchClients = async () => {
    setLoadingClients(true)
    try {
      setClients(await fetchClientsWithEmails())
    } catch (error) {
      console.error('Erreur lors de la récupération des clients:', error)
      setClients([])
    } finally {
      setLoadingClients(false)
    }
  }

  // Update client info when clients are loaded
  useEffect(() => {
    if (clients.length > 0 && project && project.client_id) {
      const currentClient = clients.find(c => String(c.id) === String(project.client_id))
      if (currentClient) {
        setFormData(prev => ({
          ...prev,
          clientId: String(currentClient.id),
          clientNom: currentClient.nom,
          clientEmailExisting: currentClient.emails[0] || '',
        }))
      }
    }
  }, [clients, project])

  const fetchProjectManagers = async () => {
    setLoadingManagers(true)
    try {
      const response = await api.get<{ data: User[]; count: number }>('/users/project-managers')
      const pmsData = response.data.data || []
      setProjectManagers(pmsData)
    } catch (error) {
      console.error('Erreur lors de la récupération des chefs de projet:', error)
      setProjectManagers([])
    } finally {
      setLoadingManagers(false)
    }
  }

  const fetchPeople = async () => {
    setLoadingPeople(true)
    try {
      const response = await api.get<{ data: User[]; count: number }>('/users', { internal: 'true' })
      const users = response.data.data || []
      setPeople(
        users
          .filter((item) => item.role === 'admin' || item.role === 'super_admin')
          .map((item) => personFromName(item.id, item.nom))
          .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
      )
    } catch (error) {
      console.error('Erreur lors de la récupération des développeurs:', error)
      setPeople([])
    } finally {
      setLoadingPeople(false)
    }
  }

  const fetchActivitySectors = async () => {
    setLoadingSectors(true)
    try {
      const response = await api.get<{ data: ActivitySector[]; count: number }>('/activity-sectors')
      const sectors = response.data.data || []
      setActivitySectors(sectors)
    } catch (error) {
      console.error('Erreur lors de la récupération des secteurs d\'activité:', error)
      setActivitySectors([])
    } finally {
      setLoadingSectors(false)
    }
  }

  const handleCreateSector = async () => {
    if (!newSectorName.trim()) {
      alert('Veuillez entrer un nom pour le secteur d\'activité')
      return
    }

    setCreatingSector(true)
    try {
      const response = await api.post<ActivitySector>('/activity-sectors', {
        nom: newSectorName.trim()
      })
      const newSector = response.data
      
      // Add to list and select it
      setActivitySectors(prev => [...prev, newSector])
      setFormData(prev => ({
        ...prev,
        activitySectorId: String(newSector.id)
      }))
      setNewSectorName('')
      setShowNewSectorInput(false)
    } catch (error: any) {
      console.error('Erreur lors de la création du secteur:', error)
      const errorMessage = error?.message || error?.response?.data?.detail || 'Erreur lors de la création du secteur'
      alert(errorMessage)
    } finally {
      setCreatingSector(false)
    }
  }

  const handleInputChange = (field: keyof ProjectFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  // URL management functions
  const addUrl = (type: 'github' | 'server' | 'figma' | 'notion') => {
    if (type === 'github') {
      setGithubUrls(prev => [...prev, ''])
    } else if (type === 'server') {
      setServerUrls(prev => [...prev, ''])
    } else if (type === 'figma') {
      setFigmaUrls(prev => [...prev, ''])
    } else {
      setNotionUrls(prev => [...prev, ''])
    }
  }

  const removeUrl = (type: 'github' | 'server' | 'figma' | 'notion', index: number) => {
    if (type === 'github') {
      setGithubUrls(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : [''])
    } else if (type === 'server') {
      setServerUrls(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : [''])
    } else if (type === 'figma') {
      setFigmaUrls(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : [''])
    } else {
      setNotionUrls(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : [''])
    }
  }

  const updateUrl = (type: 'github' | 'server' | 'figma' | 'notion', index: number, value: string) => {
    if (type === 'github') {
      setGithubUrls(prev => prev.map((url, i) => i === index ? value : url))
    } else if (type === 'server') {
      setServerUrls(prev => prev.map((url, i) => i === index ? value : url))
    } else if (type === 'figma') {
      setFigmaUrls(prev => prev.map((url, i) => i === index ? value : url))
    } else {
      setNotionUrls(prev => prev.map((url, i) => i === index ? value : url))
    }
  }

  const handleSubmit = async () => {
    // Filter out empty URLs
    const validGithubUrls = githubUrls.filter(url => url.trim())
    const validServerUrls = serverUrls.filter(url => url.trim())
    const validFigmaUrls = figmaUrls.filter(url => url.trim())
    const validNotionUrls = notionUrls.filter(url => url.trim())

    const emailsToAdd = newClientEmails.map(e => e.trim()).filter(Boolean)
    const invalidEmail = emailsToAdd.find(e => !isValidEmail(e))
    if (invalidEmail) {
      alert(`Email invalide : ${invalidEmail}`)
      return
    }

    if (projectManagerIds.length === 0) {
      alert('Sélectionnez au moins un chef de projet')
      return
    }

    await onSubmit({
      ...formData,
      projectManagerId: projectManagerIds[0],
      projectManagerIds,
      developerIds,
      githubUrls: validGithubUrls,
      serverUrls: validServerUrls,
      figmaUrls: validFigmaUrls,
      notionUrls: validNotionUrls,
      newClientEmails: emailsToAdd,
    })
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  // Chefs de projet existants + collaborateurs internes (un collaborateur choisi devient chef de projet)
  const projectManagerPeople: Person[] = (() => {
    const byId = new Map<string, Person>()
    for (const pm of projectManagers) byId.set(pm.id, personFromName(pm.id, pm.nom))
    for (const person of people) if (!byId.has(person.id)) byId.set(person.id, person)
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, 'fr'))
  })()

  // Get all status options
  const getAllStatusOptions = () => {
    return enumToSelectOptions(ProjectStatus, ProjectStatusLabels)
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      hideHeader={true}
      contentClassName="border-4 border-cyan-400 rounded-2xl shadow-2xl"
      footer={
        <div className="flex gap-3 pt-4 justify-start">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={isLoading}
            className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white hover:from-cyan-500 hover:to-cyan-600 shadow-md"
          >
            Mettre à jour le projet
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="bg-white border-b border-gray-200 px-6 py-4 -mx-6 -mt-6 mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Modifier le projet</h2>
            <p className="text-sm text-gray-500 mt-1">
              Mettez à jour les informations du projet
            </p>
          </div>
        </div>

        <FormField label="Client" required>
          <SearchableSelect
            value={formData.clientId || ''}
            onChange={(value) => {
              const selectedClient = clients.find(c => c.id === value)
              setFormData(prev => ({
                ...prev,
                clientId: value,
                clientNom: selectedClient?.nom || '',
                clientEmailExisting: selectedClient?.emails[0] || ''
              }))
              setNewClientEmails([])
            }}
            options={clients.map(client => ({
              value: client.id,
              label: formatClientLabel(client.nom, client.emails)
            }))}
            placeholder={loadingClients ? "Chargement..." : clients.length === 0 ? "Aucun client disponible" : "Sélectionner un client"}
            searchPlaceholder="Rechercher un client..."
            disabled={loadingClients || clients.length === 0}
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
          />
        </FormField>

        {formData.clientId && (
          <FormField label="Emails du client">
            <div className="space-y-2">
              {(clients.find(c => String(c.id) === String(formData.clientId))?.emails || []).map(email => (
                <div key={email} className="flex items-center gap-2 text-sm text-gray-600 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                  <Mail className="w-4 h-4 text-gray-400" />
                  {email}
                </div>
              ))}
              {newClientEmails.map((email, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setNewClientEmails(prev => prev.map((v, i) => i === index ? e.target.value : v))}
                      placeholder="nouveau@exemple.com"
                      className={`bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent pl-10 ${
                        email && !isValidEmail(email.trim()) ? 'border-red-300' : ''
                      }`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setNewClientEmails(prev => prev.filter((_, i) => i !== index))}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Retirer cet email"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setNewClientEmails(prev => [...prev, ''])}
                className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700 mt-2"
              >
                <Plus className="w-4 h-4" />
                Ajouter un email
              </button>
              <p className="text-xs text-gray-500">
                💡 Chaque nouvel email pourra se connecter à la plateforme et accéder aux projets du client.
              </p>
            </div>
          </FormField>
        )}

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">Secteur d'activité</label>
          
          {showNewSectorInput ? (
            <div className="space-y-4 border border-blue-200 rounded-lg p-4 bg-blue-50/30">
              <FormField label="Nom du nouveau secteur" required>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={newSectorName}
                    onChange={(e) => setNewSectorName(e.target.value)}
                    placeholder="Ex: E-commerce, Fintech, Santé..."
                    className="bg-white border-gray-200 focus:ring-cyan-400 focus:border-transparent flex-1"
                  />
                  <Button
                    type="button"
                    onClick={handleCreateSector}
                    isLoading={creatingSector}
                    disabled={!newSectorName.trim() || creatingSector}
                    className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white hover:from-cyan-500 hover:to-cyan-600 shadow-md"
                  >
                    Créer
                  </Button>
                </div>
              </FormField>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowNewSectorInput(false)
                  setNewSectorName('')
                }}
                className="text-sm"
              >
                Annuler
              </Button>
            </div>
          ) : (
            <SearchableSelect
              value={formData.activitySectorId || ''}
              onChange={(value) => handleInputChange('activitySectorId', value)}
              options={activitySectors.map(sector => ({
                value: String(sector.id),
                label: sector.nom
              }))}
              placeholder={loadingSectors ? "Chargement..." : "Sélectionner un secteur d'activité"}
              searchPlaceholder="Rechercher un secteur..."
              disabled={loadingSectors}
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
              showAddButton={true}
              addButtonLabel="Ajouter un secteur"
              onAddClick={() => setShowNewSectorInput(true)}
            />
          )}
        </div>

        <FormField 
          label="Chef de projet" 
          required
        >
          <PeopleSelect
            people={projectManagerPeople}
            selectedIds={projectManagerIds}
            onToggle={(id) =>
              setProjectManagerIds((current) =>
                current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
              )
            }
            loading={loadingManagers || loadingPeople}
            loadingLabel="Chargement des chefs de projet…"
            placeholder="Sélectionner un ou plusieurs chefs de projet"
            searchPlaceholder="Rechercher un chef de projet..."
            emptyLabel="Aucun chef de projet"
          />
        </FormField>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Nom du projet" required>
            <Input
              type="text"
              value={formData.nomProjet}
              onChange={(e) => handleInputChange('nomProjet', e.target.value)}
              placeholder="Ex: Plateforme IA Analytics"
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
            />
          </FormField>

          <FormField label="Date de kickoff" required>
            <Input
              type="date"
              value={formData.dateKickoff}
              onChange={(e) => handleInputChange('dateKickoff', e.target.value)}
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
            />
          </FormField>
        </div>

        <FormField label="Description" required>
          <Textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Description du projet visible par le client"
            rows={4}
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent resize-none"
          />
        </FormField>

        {/* Description interne - visible uniquement pour les super admins */}
        {isSuperAdmin && (
          <FormField label="Description interne (Super Admin uniquement)">
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-cyan-500" />
                <span className="text-xs text-gray-500">Visible uniquement par les super admins</span>
              </div>
              <Textarea
                value={formData.descriptionInterne || ''}
                onChange={(e) => handleInputChange('descriptionInterne', e.target.value)}
                placeholder="Description interne du projet (non visible par le client)"
                rows={4}
                className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent resize-none"
              />
            </div>
          </FormField>
        )}

        <FormField label="Statut">
          <Select
            value={formData.status || ''}
            onChange={(e) => handleInputChange('status', e.target.value)}
            placeholder="Sélectionner un statut"
            options={getAllStatusOptions()}
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
          />
        </FormField>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Stack technique (séparés par virgule)">
            <Input
              type="text"
              value={formData.stackTechnique}
              onChange={(e) => handleInputChange('stackTechnique', e.target.value)}
              placeholder="React, TypeScript, Supabase"
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
            />
          </FormField>

          <FormField label="Développeurs">
            <PeopleSelect
              people={people}
              selectedIds={developerIds}
              onToggle={(id) =>
                setDeveloperIds((current) =>
                  current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
                )
              }
              loading={loadingPeople}
              loadingLabel="Chargement des développeurs…"
              placeholder="Sélectionner des développeurs"
              searchPlaceholder="Rechercher un développeur..."
              emptyLabel="Aucun développeur"
            />
          </FormField>
        </div>

        {/* GitHub URLs */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold text-gray-700">URLs GitHub (optionnel)</label>
            <button
              type="button"
              onClick={() => addUrl('github')}
              className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Ajouter
            </button>
          </div>
          <div className="space-y-2">
            {githubUrls.map((url, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  type="url"
                  value={url}
                  onChange={(e) => updateUrl('github', index, e.target.value)}
                  placeholder="https://github.com/..."
                  className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent flex-1"
                />
                {githubUrls.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeUrl('github', index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Server URLs */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold text-gray-700">URLs Serveur (optionnel)</label>
            <button
              type="button"
              onClick={() => addUrl('server')}
              className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Ajouter
            </button>
          </div>
          <div className="space-y-2">
            {serverUrls.map((url, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  type="url"
                  value={url}
                  onChange={(e) => updateUrl('server', index, e.target.value)}
                  placeholder="https://app.exemple.com"
                  className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent flex-1"
                />
                {serverUrls.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeUrl('server', index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Figma URLs */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold text-gray-700">URLs Figma (optionnel)</label>
            <button
              type="button"
              onClick={() => addUrl('figma')}
              className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Ajouter
            </button>
          </div>
          <div className="space-y-2">
            {figmaUrls.map((url, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  type="url"
                  value={url}
                  onChange={(e) => updateUrl('figma', index, e.target.value)}
                  placeholder="https://figma.com/..."
                  className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent flex-1"
                />
                {figmaUrls.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeUrl('figma', index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Notion URLs */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold text-gray-700">URLs Notion (optionnel)</label>
            <button
              type="button"
              onClick={() => addUrl('notion')}
              className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Ajouter
            </button>
          </div>
          <div className="space-y-2">
            {notionUrls.map((url, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  type="url"
                  value={url}
                  onChange={(e) => updateUrl('notion', index, e.target.value)}
                  placeholder="https://www.notion.so/..."
                  className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent flex-1"
                />
                {notionUrls.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeUrl('notion', index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  )
}
